import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Search, ArrowLeft, User, Phone, PhoneCall, Mail, MapPin, Calendar, AlertTriangle, Plus, X as XIcon, Trash2, Send, Loader2, CheckCircle2, XCircle, UserPlus, Link2, Copy, Clock } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { AgentAvatar } from '@/components/agent/AgentAvatar';
import ContactSmsCard from '@/components/sms/ContactSmsCard';
import { prospectAPI, agencyAPI, agentOnboardingAPI } from '@/lib/api';
import { getErrorMessage, formatPhone, formatPhoneInput } from '@/lib/utils';

const STATUSES = ['new','contacted','qualified','interview','application','licensing','contracted','not_a_fit','duplicate','converted','archived'];
const STATUS_LABELS = {
  new: 'New',
  contacted: 'Contacted',
  qualified: 'Qualified',
  interview: 'Interview',
  application: 'Application',
  licensing: 'Licensing',
  contracted: 'Contracted',
  not_a_fit: 'Not a fit',
  duplicate: 'Duplicate',
  converted: 'Converted',
  archived: 'Archived',
};
const CALL_OUTCOME_LABELS = {
  connected: 'Connected',
  no_answer: 'No answer',
  voicemail: 'Left voicemail',
  busy: 'Busy',
  callback: 'Call back requested',
  wrong_number: 'Wrong number',
};
const SOURCE_TYPES = [
  { value: 'all', label: 'All sources' },
  { value: 'manual', label: 'Manual (added by KR)' },
  { value: 'public_landing', label: 'Public landing (Become an Agent)' },
  { value: 'event_share_link', label: 'Event share link' },
];
const AREAS_OF_INTEREST = [
  'Medicare', 'ACA / Marketplace', 'Life Insurance', 'Annuities',
  'Dental and Vision', 'Supplemental Products', 'Agency Partnership',
];
const STATUS_TONE = {
  new: 'bg-slate-100 text-slate-700',
  contacted: 'bg-sky-100 text-sky-800',
  qualified: 'bg-emerald-100 text-emerald-800',
  interview: 'bg-violet-100 text-violet-800',
  application: 'bg-blue-100 text-blue-800',
  licensing: 'bg-amber-100 text-amber-900',
  contracted: 'bg-teal-100 text-teal-800',
  not_a_fit: 'bg-rose-100 text-rose-800',
  duplicate: 'bg-amber-100 text-amber-800',
  converted: 'bg-indigo-100 text-indigo-800',
  archived: 'bg-slate-100 text-slate-500',
};

export default function ProspectsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  // Phase 27.36 — Allow the Event Workspace Prospect Drawer to deep-link
  // back to this page filtered to a specific event.
  const sourceEventId = searchParams.get('source_event_id') || '';
  const [status, setStatus] = useState('all');
  const [sourceType, setSourceType] = useState('all');
  const [search, setSearch] = useState('');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // prospect_id
  // Phase 27.30.31 — Manual pipeline entry
  const [showCreate, setShowCreate] = useState(false);
  const [movingId, setMovingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: 1, page_size: 50, status, source_type: sourceType, search };
      if (sourceEventId) params.source_event_id = sourceEventId;
      const r = await prospectAPI.list(params);
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to load prospects'));
    } finally {
      setLoading(false);
    }
  }, [status, sourceType, search, sourceEventId]);

  useEffect(() => { load(); }, [load]);

  // Derive a friendly event title from the first prospect row so we can
  // show a helpful "Filtered by event" chip without another API round-trip.
  const filteredEventTitle = useMemo(() => {
    if (!sourceEventId) return null;
    const hit = items.find((p) => p.source_event_id === sourceEventId);
    return hit?.source_event_title || sourceEventId;
  }, [sourceEventId, items]);

  const clearEventFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('source_event_id');
    setSearchParams(next, { replace: true });
  };

  const handlePipelineChange = async (prospectId, newStatus) => {
    setMovingId(prospectId);
    try {
      await prospectAPI.update(prospectId, { status: newStatus });
      toast.success(`Moved to ${STATUS_LABELS[newStatus] || newStatus}`);
      setItems((prev) => prev.map((p) => (p.prospect_id === prospectId ? { ...p, status: newStatus } : p)));
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not move prospect'));
    } finally {
      setMovingId(null);
    }
  };

  if (selected) {
    return <ProspectDetail prospectId={selected} onBack={() => { setSelected(null); load(); }} />;
  }

  return (
    <div data-testid="prospects-page">
      <PageHeader
        title="Prospects"
        description="Recruiting pipeline — public form submissions, event walk-ins, and manual entries."
        breadcrumbs={[{ label: 'Prospects', href: '/prospects' }]}
        actions={(
          <Button size="sm" onClick={() => setShowCreate(true)} data-testid="prospects-new-btn">
            <Plus size={14} className="mr-1.5" /> New Prospect
          </Button>
        )}
      />
      <div className="page-content space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search name, email, phone, state…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && load()}
              className="pl-9 h-9 text-sm"
              data-testid="prospects-search"
            />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-9 w-40 text-xs" data-testid="prospects-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={sourceType} onValueChange={setSourceType}>
            <SelectTrigger className="h-9 w-56 text-xs" data-testid="prospects-source-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SOURCE_TYPES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="ml-auto text-xs text-slate-500">{total} record{total === 1 ? '' : 's'}</div>
        </div>

        {sourceEventId && (
          <div
            className="flex items-center gap-2 p-2 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-900"
            data-testid="prospects-event-filter-chip"
          >
            <Calendar size={12} />
            <span>
              Filtered by event:{' '}
              <span className="font-semibold">{filteredEventTitle || sourceEventId}</span>
            </span>
            <button
              type="button"
              onClick={clearEventFilter}
              className="ml-auto p-1 rounded hover:bg-amber-100"
              aria-label="Clear event filter"
              data-testid="prospects-event-filter-clear"
            >
              <XIcon size={12} />
            </button>
          </div>
        )}

        {loading ? (
          <p className="py-16 text-center text-sm text-slate-500">Loading…</p>
        ) : items.length === 0 ? (
          <Card>
            <CardContent className="py-14 text-center" data-testid="prospects-empty">
              <User size={28} className="mx-auto text-slate-400 mb-2" />
              <p className="text-sm text-slate-600">No prospect submissions yet.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="overflow-x-auto border border-slate-200 rounded-lg bg-white" data-testid="prospects-table">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                  <th className="px-3 py-2">Name</th>
                  <th className="px-3 py-2">Contact</th>
                  <th className="px-3 py-2">Source</th>
                  <th className="px-3 py-2">State</th>
                  <th className="px-3 py-2">Licensed</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Submitted</th>
                  <th className="px-3 py-2 w-8"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((p) => (
                  <tr key={p.prospect_id} className="hover:bg-slate-50 cursor-pointer"
                    onClick={() => setSelected(p.prospect_id)}
                    data-testid={`prospect-row-${p.prospect_id}`}>
                    <td className="px-3 py-2">
                      <div className="font-medium text-slate-900">{p.first_name} {p.last_name}</div>
                      {p.is_duplicate && <Badge className="text-[10px] mt-1 bg-amber-100 text-amber-800"><AlertTriangle size={10} className="mr-1" /> Possible duplicate</Badge>}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <div>{p.email}</div>
                      <div className="text-slate-500">{formatPhone(p.phone)}</div>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {p.source_type === 'event_share_link' ? (
                        <div>
                          <Badge className="text-[10px] bg-indigo-100 text-indigo-800">Event share</Badge>
                          <div className="text-slate-500 mt-0.5 max-w-[180px] truncate" title={p.source_event_title}>
                            {p.source_event_title || '—'}
                          </div>
                          {p.referred_by_name && (
                            <div className="flex items-center gap-1.5 text-slate-400 text-[10px] mt-0.5">
                              <span>by</span>
                              {p.referred_by_photo_url ? (
                                <AgentAvatar
                                  agent={{
                                    npn: p.referred_by_npn,
                                    first_name: (p.referred_by_name || '').split(' ')[0] || '',
                                    last_name: (p.referred_by_name || '').split(' ').slice(1).join(' ') || '',
                                    photo_url: p.referred_by_photo_url,
                                  }}
                                  size="w-5 h-5"
                                  rounded="full"
                                  textSize="text-[8px]"
                                  testId={`prospect-referrer-avatar-${p.prospect_id}`}
                                />
                              ) : null}
                              <span className="truncate max-w-[130px]">{p.referred_by_name}</span>
                            </div>
                          )}
                        </div>
                      ) : p.source_type === 'manual' ? (
                        <div>
                          <Badge className="text-[10px] bg-purple-100 text-purple-800">Manual</Badge>
                          {p.created_by_name && (
                            <div className="text-slate-400 text-[10px] mt-0.5 truncate max-w-[160px]">
                              by {p.created_by_name}
                            </div>
                          )}
                        </div>
                      ) : (
                        <Badge className="text-[10px] bg-slate-100 text-slate-700">Public form</Badge>
                      )}
                    </td>
                    <td className="px-3 py-2">{p.state || '—'}</td>
                    <td className="px-3 py-2">{p.is_licensed ? 'Yes' : 'No'}</td>
                    <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                      <Select
                        value={p.status}
                        onValueChange={(v) => handlePipelineChange(p.prospect_id, v)}
                        disabled={movingId === p.prospect_id}
                      >
                        <SelectTrigger
                          className={`h-7 w-32 text-[10px] font-semibold border-0 ${STATUS_TONE[p.status] || ''}`}
                          data-testid={`prospect-pipeline-${p.prospect_id}`}
                        >
                          <SelectValue>{STATUS_LABELS[p.status] || p.status}</SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {STATUSES.map((s) => (
                            <SelectItem key={s} value={s} className="text-xs">
                              {STATUS_LABELS[s]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">
                      {p.submitted_at ? new Date(p.submitted_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-3 py-2 text-right text-slate-400">›</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <NewProspectDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={() => { setShowCreate(false); load(); }}
      />
    </div>
  );
}

// Phase 27.30.31 — Manual pipeline entry dialog
function NewProspectDialog({ open, onClose, onCreated }) {
  const initial = useMemo(() => ({
    first_name: '', last_name: '', email: '', phone: '', state: '',
    is_licensed: false, npn: '', current_agency: '', years_experience: '',
    preferred_language: '', comments: '', initial_status: 'new',
    areas_of_interest: [],
  }), []);
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setForm(initial); }, [open, initial]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const toggleArea = (area) => setForm((f) => ({
    ...f,
    areas_of_interest: f.areas_of_interest.includes(area)
      ? f.areas_of_interest.filter((a) => a !== area)
      : [...f.areas_of_interest, area],
  }));

  const canSubmit = form.first_name.trim() && form.last_name.trim()
    && form.email.trim() && form.phone.trim() && form.state.trim();

  const submit = async () => {
    if (!canSubmit) {
      toast.error('First name, last name, email, phone and state are required.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        state: form.state.trim(),
        is_licensed: !!form.is_licensed,
        areas_of_interest: form.areas_of_interest,
        npn: form.npn.trim() || null,
        current_agency: form.current_agency.trim() || null,
        years_experience: form.years_experience === '' ? null : Number(form.years_experience),
        preferred_language: form.preferred_language.trim() || null,
        comments: form.comments.trim() || null,
        initial_status: form.initial_status,
      };
      await prospectAPI.create(payload);
      toast.success('Prospect added to pipeline');
      onCreated?.();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not create prospect'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-2xl" data-testid="new-prospect-dialog">
        <DialogHeader>
          <DialogTitle>New Prospect</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            <FormField label="First name *">
              <Input value={form.first_name} onChange={set('first_name')} data-testid="np-first-name" />
            </FormField>
            <FormField label="Last name *">
              <Input value={form.last_name} onChange={set('last_name')} data-testid="np-last-name" />
            </FormField>
            <FormField label="Email *">
              <Input type="email" value={form.email} onChange={set('email')} data-testid="np-email" />
            </FormField>
            <FormField label="Phone *">
              <Input value={formatPhoneInput(form.phone)} onChange={set('phone')} data-testid="np-phone" />
            </FormField>
            <FormField label="State *">
              <Input value={form.state} onChange={set('state')} placeholder="e.g. FL" data-testid="np-state" />
            </FormField>
            <FormField label="NPN (optional)">
              <Input value={form.npn} onChange={set('npn')} data-testid="np-npn" />
            </FormField>
            <FormField label="Current agency">
              <Input value={form.current_agency} onChange={set('current_agency')} data-testid="np-agency" />
            </FormField>
            <FormField label="Years experience">
              <Input type="number" min={0} max={80} value={form.years_experience} onChange={set('years_experience')} data-testid="np-years" />
            </FormField>
            <FormField label="Preferred language">
              <Input value={form.preferred_language} onChange={set('preferred_language')} placeholder="English / Spanish / ..." />
            </FormField>
            <FormField label="Initial pipeline status">
              <Select value={form.initial_status} onValueChange={(v) => setForm((f) => ({ ...f, initial_status: v }))}>
                <SelectTrigger data-testid="np-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}
                </SelectContent>
              </Select>
            </FormField>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="np-licensed"
              checked={form.is_licensed}
              onCheckedChange={(v) => setForm((f) => ({ ...f, is_licensed: !!v }))}
              data-testid="np-licensed"
            />
            <Label htmlFor="np-licensed" className="text-sm cursor-pointer">Currently licensed</Label>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Areas of interest</Label>
            <div className="grid grid-cols-2 gap-1.5 mt-2">
              {AREAS_OF_INTEREST.map((area) => (
                <label key={area} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox
                    checked={form.areas_of_interest.includes(area)}
                    onCheckedChange={() => toggleArea(area)}
                  />
                  <span>{area}</span>
                </label>
              ))}
            </div>
          </div>
          <FormField label="Notes / context">
            <Textarea rows={3} value={form.comments} onChange={set('comments')}
              placeholder="How did you meet this candidate? Anything KR should know when reaching out?"
              data-testid="np-comments"
            />
          </FormField>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={!canSubmit || saving} data-testid="np-submit">
            {saving ? 'Adding…' : 'Add to pipeline'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FormField({ label, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</Label>
      {children}
    </div>
  );
}

function RemindersCard({ prospectId, reminders, onChange }) {
  const [due, setDue] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const today = new Date().toISOString().slice(0, 10);

  const add = async () => {
    if (!due) { toast.error('Pick a due date'); return; }
    setBusy(true);
    try {
      await prospectAPI.addReminder(prospectId, { due_date: due, note: note.trim() });
      setDue(''); setNote('');
      toast.success('Reminder added');
      await onChange();
    } catch (e) { toast.error(getErrorMessage(e, 'Could not add reminder')); }
    finally { setBusy(false); }
  };
  const complete = async (id) => {
    try { await prospectAPI.completeReminder(prospectId, id); await onChange(); }
    catch (e) { toast.error(getErrorMessage(e, 'Failed')); }
  };
  const remove = async (id) => {
    try { await prospectAPI.deleteReminder(prospectId, id); await onChange(); }
    catch (e) { toast.error(getErrorMessage(e, 'Failed')); }
  };

  const sorted = [...(reminders || [])].sort((a, b) => {
    if (a.done !== b.done) return a.done ? 1 : -1;
    return (a.due_date || '').localeCompare(b.due_date || '');
  });

  return (
    <Card data-testid="prospect-reminders-card"><CardContent className="p-5">
      <SectionTitle>Follow-up reminders</SectionTitle>
      <div className="flex flex-col sm:flex-row gap-2 mt-3">
        <Input type="date" value={due} min={today} onChange={(e) => setDue(e.target.value)} className="h-9 sm:w-40" data-testid="reminder-due-input" />
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Call about licensing" className="h-9 flex-1" data-testid="reminder-note-input" />
        <Button size="sm" className="h-9" onClick={add} disabled={busy} data-testid="reminder-add-btn"><Plus size={14} className="mr-1" /> Add</Button>
      </div>
      {sorted.length === 0 ? (
        <p className="text-xs text-slate-400 mt-3">No reminders yet.</p>
      ) : (
        <ul className="mt-3 space-y-2" data-testid="reminder-list">
          {sorted.map((r) => {
            const overdue = !r.done && r.due_date < today;
            return (
              <li key={r.reminder_id} className={`flex items-center gap-2 text-sm rounded-md border px-3 py-2 ${r.done ? 'border-slate-100 bg-slate-50 text-slate-400' : overdue ? 'border-rose-200 bg-rose-50' : 'border-slate-200'}`} data-testid={`reminder-${r.reminder_id}`}>
                <Calendar size={13} className={overdue ? 'text-rose-500' : 'text-slate-400'} />
                <span className={`font-medium ${r.done ? 'line-through' : overdue ? 'text-rose-700' : 'text-slate-800'}`}>{r.due_date}</span>
                {overdue && <Badge className="text-[9px] bg-rose-100 text-rose-700">Overdue</Badge>}
                <span className={`flex-1 truncate ${r.done ? 'line-through' : 'text-slate-600'}`}>{r.note || ''}</span>
                {!r.done && (
                  <button className="text-xs font-medium text-emerald-700 hover:underline" onClick={() => complete(r.reminder_id)} data-testid={`reminder-done-${r.reminder_id}`}>Done</button>
                )}
                <button className="text-slate-400 hover:text-rose-600" onClick={() => remove(r.reminder_id)} data-testid={`reminder-delete-${r.reminder_id}`}><Trash2 size={13} /></button>
              </li>
            );
          })}
        </ul>
      )}
    </CardContent></Card>
  );
}

function RecruitingEmailCard({ prospectId, prospect, emails, onSent }) {
  const [templates, setTemplates] = useState([]);
  const [fromEmail, setFromEmail] = useState('');
  const [configured, setConfigured] = useState(true);
  const [templateId, setTemplateId] = useState('');
  const defaultLang = useMemo(() => {
    const pref = (prospect.preferred_language || '').toLowerCase();
    if (pref.startsWith('en') || pref.includes('ingl') || pref.includes('english')) return 'en';
    return 'es';
  }, [prospect.preferred_language]);
  const [language, setLanguage] = useState(defaultLang);
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    prospectAPI.emailTemplates()
      .then((r) => {
        setTemplates(r.data.templates || []);
        setFromEmail(r.data.from_email || '');
        setConfigured(r.data.configured !== false);
        if ((r.data.templates || []).length && !templateId) setTemplateId(r.data.templates[0].id);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const send = async () => {
    if (!templateId) { toast.error('Pick a template'); return; }
    setSending(true);
    try {
      await prospectAPI.sendEmail(prospectId, { template_id: templateId, language, personal_note: note.trim() || undefined });
      toast.success('Email sent');
      setNote('');
      await onSent();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not send email'));
    } finally {
      setSending(false);
    }
  };

  const labelFor = (t) => (language === 'en' ? t.label_en : t.label_es);
  const sortedEmails = [...(emails || [])].sort((a, b) => (b.sent_at || '').localeCompare(a.sent_at || ''));

  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2">
        <Card data-testid="prospect-email-card"><CardContent className="p-5 space-y-4">
          <div className="flex items-center justify-between">
            <SectionTitle>Send recruiting email</SectionTitle>
            {fromEmail && <span className="text-[11px] text-slate-500">From: {fromEmail}</span>}
          </div>
          {!prospect.email ? (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              This prospect has no email on file.
            </p>
          ) : (
            <>
              <p className="text-xs text-slate-500">To: <span className="font-medium text-slate-700">{prospect.email}</span></p>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-wider text-slate-500">Template</label>
                  <Select value={templateId} onValueChange={setTemplateId}>
                    <SelectTrigger className="mt-1" data-testid="email-template-select"><SelectValue placeholder="Choose a message" /></SelectTrigger>
                    <SelectContent>
                      {templates.map((t) => <SelectItem key={t.id} value={t.id}>{labelFor(t)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-slate-500">Language</label>
                  <Select value={language} onValueChange={setLanguage}>
                    <SelectTrigger className="mt-1" data-testid="email-language-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="es">Español</SelectItem>
                      <SelectItem value="en">English</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-500">Personal note (optional)</label>
                <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)}
                  placeholder={language === 'en' ? 'Added as a highlighted note inside the email.' : 'Se agrega como nota destacada dentro del correo.'}
                  data-testid="email-note-input" />
              </div>
              <Button onClick={send} disabled={sending || !templateId || !configured}
                className="bg-[#0f2a5e] hover:bg-[#0a1f47]" data-testid="email-send-btn">
                {sending ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Send size={14} className="mr-1.5" />}
                {sending ? 'Sending…' : 'Send email'}
              </Button>
              {!configured && <p className="text-xs text-rose-600">Email sending is not configured on the server.</p>}
            </>
          )}
        </CardContent></Card>
      </div>

      <div>
        <Card><CardContent className="p-5">
          <SectionTitle>Sent emails</SectionTitle>
          {sortedEmails.length === 0 ? (
            <p className="text-xs text-slate-400 mt-3">No emails sent yet.</p>
          ) : (
            <ul className="mt-3 space-y-2" data-testid="email-history-list">
              {sortedEmails.map((e) => (
                <li key={e.email_id} className="text-sm border border-slate-200 rounded-md px-3 py-2">
                  <div className="flex items-center gap-1.5">
                    {e.status === 'sent'
                      ? <CheckCircle2 size={13} className="text-emerald-600" />
                      : <XCircle size={13} className="text-rose-600" />}
                    <span className="font-medium text-slate-800 truncate">{e.subject}</span>
                    <Badge className="text-[9px] bg-slate-100 text-slate-600 uppercase">{e.language}</Badge>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1">
                    {e.sent_by_name || 'KR'} · {new Date(e.sent_at).toLocaleString()}
                  </div>
                  {e.personal_note && <div className="text-xs text-slate-600 mt-1 italic">"{e.personal_note}"</div>}
                </li>
              ))}
            </ul>
          )}
        </CardContent></Card>
      </div>
    </div>
  );
}

function ConvertProspectDialog({ open, onOpenChange, prospect, prospectId, onConverted }) {
  const [npn, setNpn] = useState('');
  const [agencyId, setAgencyId] = useState('');
  const [agencies, setAgencies] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setNpn(prospect?.npn || '');
    setResult(null);
    agencyAPI.list()
      .then((r) => {
        const list = r.data?.items || r.data || [];
        setAgencies(list);
        if (!agencyId && list.length) setAgencyId(list[0].agency_id);
      })
      .catch(() => setAgencies([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = async () => {
    const cleanNpn = (npn || '').replace(/\D/g, '');
    if (!cleanNpn) { toast.error('An NPN (digits only) is required to convert.'); return; }
    setBusy(true);
    try {
      const r = await prospectAPI.convert(prospectId, { npn: cleanNpn, agency_id: agencyId || undefined });
      setResult(r.data);
      toast.success('Prospect converted — onboarding invite issued.');
      onConverted?.();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Conversion failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="prospect-convert-dialog">
        <DialogHeader>
          <DialogTitle>Convert to agent</DialogTitle>
        </DialogHeader>
        {result ? (
          <div className="space-y-3" data-testid="prospect-convert-result">
            <div className="flex items-center gap-2 text-emerald-700">
              <CheckCircle2 size={18} /><span className="font-semibold">Onboarding started</span>
            </div>
            <p className="text-sm text-slate-600">
              A self-service onboarding link was issued for NPN <strong>{result.npn}</strong> at{' '}
              <strong>{result.agency_name}</strong>.
              {result.email_sent
                ? ' An invite email was sent to the prospect.'
                : ' The invite email could not be sent — share the link below manually.'}
            </p>
            <div className="rounded-md border border-slate-200 bg-slate-50 p-2 text-[11px] break-all" data-testid="prospect-convert-link">
              {result.onboard_link}
            </div>
            <DialogFooter>
              <Button onClick={() => onOpenChange(false)} className="bg-[#0f2a5e]" data-testid="prospect-convert-close">Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Issue an onboarding link for{' '}
              <strong>{prospect?.first_name} {prospect?.last_name}</strong> ({prospect?.email}). They'll
              set their password and complete their profile. The prospect will be marked <em>Converted</em>.
            </p>
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500">NPN *</label>
              <Input value={npn} onChange={(e) => setNpn(e.target.value)} placeholder="Digits only"
                className="mt-1" data-testid="prospect-convert-npn" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500">Agency</label>
              <Select value={agencyId} onValueChange={setAgencyId}>
                <SelectTrigger className="mt-1" data-testid="prospect-convert-agency"><SelectValue placeholder="Select agency" /></SelectTrigger>
                <SelectContent>
                  {agencies.map((a) => <SelectItem key={a.agency_id} value={a.agency_id}>{a.agency_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy} data-testid="prospect-convert-cancel">Cancel</Button>
              <Button onClick={submit} disabled={busy} className="bg-[#0f2a5e] hover:bg-[#0a1f47]" data-testid="prospect-convert-submit">
                {busy ? 'Converting…' : 'Convert & send invite'}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ProspectDetail({ prospectId, onBack }) {
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showConvert, setShowConvert] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await prospectAPI.get(prospectId);
      setP(r.data);
      setStatus(r.data.status);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to load prospect'));
      onBack();
    } finally {
      if (!silent) setLoading(false);
    }
  }, [prospectId, onBack]);

  useEffect(() => { load(); }, [load]);
  const refresh = useCallback(() => load(true), [load]);

  useEffect(() => {
    const onProspectUpdated = (e) => {
      if (!e.detail || e.detail.prospectId === prospectId) refresh();
    };
    window.addEventListener('kr:prospect-updated', onProspectUpdated);
    return () => window.removeEventListener('kr:prospect-updated', onProspectUpdated);
  }, [prospectId, refresh]);

  const save = async () => {
    setSaving(true);
    try {
      const patch = {};
      if (status !== p.status) patch.status = status;
      if (note.trim()) patch.internal_note = note.trim();
      if (Object.keys(patch).length === 0) { setSaving(false); return; }
      await prospectAPI.update(prospectId, patch);
      toast.success('Prospect updated');
      setNote('');
      await refresh();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Update failed'));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    setDeleting(true);
    try {
      await prospectAPI.remove(prospectId);
      toast.success('Prospect deleted');
      setConfirmDelete(false);
      onBack();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Delete failed'));
    } finally {
      setDeleting(false);
    }
  };

  if (loading || !p) return <p className="py-16 text-center text-sm text-slate-500">Loading…</p>;

  const pendingReminders = (p.reminders || []).filter((r) => !r.done).length;
  const isConverted = p.status === 'converted';

  const callProspect = () => {
    if (!p.phone) { toast.error('No phone number on file for this prospect'); return; }
    if (typeof window.krSoftphoneDial === 'function') {
      window.krSoftphoneDial(p.phone, {
        contact_type: 'prospect',
        contact_id: prospectId,
        contact_name: `${p.first_name} ${p.last_name}`,
      });
    } else {
      toast.error('The softphone is not available. Enable calling in Settings → Calling.');
    }
  };

  return (
    <div data-testid="prospect-detail-page">
      <PageHeader
        title={`${p.first_name} ${p.last_name}`}
        description={`${p.source} · submitted ${new Date(p.submitted_at).toLocaleString()}`}
        actions={
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={callProspect}
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="prospect-call-btn">
              <PhoneCall size={14} className="mr-1.5" /> Call
            </Button>
            <Badge className="text-[11px] bg-[#0f2a5e] text-white">{STATUS_LABELS[p.status] || p.status}</Badge>
            <Button variant="ghost" onClick={onBack}><ArrowLeft size={14} className="mr-1" /> Back</Button>
          </div>
        }
      />
      <div className="page-content">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="flex-wrap h-auto" data-testid="prospect-tabs">
            <TabsTrigger value="overview" data-testid="prospect-tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="communications" data-testid="prospect-tab-communications">SMS</TabsTrigger>
            <TabsTrigger value="email" data-testid="prospect-tab-email">Email</TabsTrigger>
            <TabsTrigger value="followups" data-testid="prospect-tab-followups">Follow-ups{pendingReminders ? ` (${pendingReminders})` : ''}</TabsTrigger>
            <TabsTrigger value="activity" data-testid="prospect-tab-activity">Activity</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-4">
            <div className="grid lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2 space-y-4">
                <Card><CardContent className="p-5 space-y-3">
                  <SectionTitle>Contact</SectionTitle>
                  <InfoRow icon={<Mail size={14} />} label="Email" value={p.email} />
                  <InfoRow icon={<Phone size={14} />} label="Phone" value={p.phone} />
                  <InfoRow icon={<MapPin size={14} />} label="State" value={p.state} />
                  <InfoRow icon={<Calendar size={14} />} label="Submitted" value={new Date(p.submitted_at).toLocaleString()} />
                </CardContent></Card>

                <Card><CardContent className="p-5 space-y-3">
                  <SectionTitle>Licensing & Interests</SectionTitle>
                  <div className="grid sm:grid-cols-2 gap-2 text-sm">
                    <div><span className="text-slate-500">Licensed:</span> {p.is_licensed ? 'Yes' : 'No'}</div>
                    <div><span className="text-slate-500">NPN:</span> {p.npn || '—'}</div>
                    <div><span className="text-slate-500">Current agency:</span> {p.current_agency || '—'}</div>
                    <div><span className="text-slate-500">Years experience:</span> {p.years_experience ?? '—'}</div>
                    <div><span className="text-slate-500">Language:</span> {p.preferred_language || '—'}</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Areas of interest</div>
                    <div className="flex flex-wrap gap-1">
                      {(p.areas_of_interest || []).length === 0 ? (
                        <span className="text-xs text-slate-500">—</span>
                      ) : p.areas_of_interest.map((a) => (
                        <Badge key={a} className="text-[10px] bg-slate-100 text-slate-700">{a}</Badge>
                      ))}
                    </div>
                  </div>
                  {p.comments && (
                    <div>
                      <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Comments</div>
                      <p className="text-sm text-slate-700 whitespace-pre-wrap">{p.comments}</p>
                    </div>
                  )}
                </CardContent></Card>

                {p.is_duplicate && (
                  <Card><CardContent className="p-5">
                    <div className="flex items-center gap-2 text-amber-800">
                      <AlertTriangle size={16} />
                      <span className="text-sm font-semibold">Possible duplicate</span>
                    </div>
                    <p className="text-xs text-slate-600 mt-1">
                      A prior prospect matched by email or phone. Original prospect id: <code className="text-[11px]">{p.duplicate_of_prospect_id || '—'}</code>
                    </p>
                  </CardContent></Card>
                )}

                <Card><CardContent className="p-5 space-y-3">
                  <SectionTitle>Consent</SectionTitle>
                  <div className="text-xs text-slate-500">Accepted at {new Date(p.consent_accepted_at).toLocaleString()} · version {p.consent_language_version}</div>
                  <div className="text-xs text-slate-500">Source: {p.source}</div>
                </CardContent></Card>
              </div>

              <aside>
                <Card><CardContent className="p-5 space-y-3">
                  <SectionTitle>Update</SectionTitle>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-slate-500">Recruiting stage</label>
                    <Select value={status} onValueChange={setStatus}>
                      <SelectTrigger className="mt-1" data-testid="prospect-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s] || s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-slate-500">Add internal note</label>
                    <Textarea rows={4} value={note} onChange={(e) => setNote(e.target.value)}
                      data-testid="prospect-note-input" placeholder="Not visible to the prospect." />
                  </div>
                  <Button
                    onClick={save}
                    disabled={saving || (status === p.status && !note.trim())}
                    className="w-full bg-[#0f2a5e] hover:bg-[#0a1f47]"
                    data-testid="prospect-save"
                  >
                    {saving ? 'Saving…' : 'Save changes'}
                  </Button>
                  <div className="pt-3 mt-1 border-t border-slate-200">
                    <div className="text-[10.5px] uppercase tracking-wider text-emerald-700 font-semibold mb-1.5">Recruiting</div>
                    <Button
                      onClick={() => setShowConvert(true)}
                      disabled={isConverted}
                      className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60"
                      data-testid="prospect-convert-btn"
                    >
                      <UserPlus size={14} className="mr-1.5" /> {isConverted ? 'Converted' : 'Convert to agent'}
                    </Button>
                    <p className="text-[10.5px] text-slate-500 mt-1.5">
                      {isConverted
                        ? 'An onboarding link has been issued for this prospect.'
                        : 'Issues an onboarding invite (NPN + email) and marks the prospect converted.'}
                    </p>
                  </div>
                  <div className="pt-3 mt-1 border-t border-slate-200">
                    <div className="text-[10.5px] uppercase tracking-wider text-rose-700 font-semibold mb-1.5">Danger zone</div>
                    <Button
                      variant="outline"
                      onClick={() => setConfirmDelete(true)}
                      className="w-full border-rose-300 text-rose-700 hover:bg-rose-50 hover:text-rose-800"
                      data-testid="prospect-delete-btn"
                    >
                      <Trash2 size={14} className="mr-1.5" /> Delete prospect
                    </Button>
                    <p className="text-[10.5px] text-slate-500 mt-1.5">
                      Permanently removes this prospect. Audit trail is preserved.
                    </p>
                  </div>
                </CardContent></Card>
              </aside>
            </div>
          </TabsContent>

          <TabsContent value="communications" className="mt-4">
            <ContactSmsCard contactType="prospect" contactId={prospectId} phone={p.phone} />
          </TabsContent>

          <TabsContent value="email" className="mt-4">
            <RecruitingEmailCard prospectId={prospectId} prospect={p} emails={p.emails || []} onSent={refresh} />
          </TabsContent>

          <TabsContent value="followups" className="mt-4">
            <RemindersCard prospectId={prospectId} reminders={p.reminders || []} onChange={refresh} />
          </TabsContent>

          <TabsContent value="activity" className="mt-4 space-y-4">
            <ProspectOnboardingCard prospectId={prospectId} prospectStatus={p.status} />
            {p.calls?.length > 0 && (
              <Card><CardContent className="p-5">
                <SectionTitle>Call history</SectionTitle>
                <ul className="space-y-3 mt-2" data-testid="prospect-call-history">
                  {[...p.calls].reverse().map((c, i) => (
                    <li key={c.call_id || i} className="text-sm text-slate-700 border-l-2 border-emerald-200 pl-3">
                      <div className="flex items-center gap-2">
                        <PhoneCall size={13} className="text-emerald-600" />
                        <span className="font-medium">{CALL_OUTCOME_LABELS[c.outcome] || c.outcome}</span>
                        {c.status_changed_to && (
                          <Badge className="text-[10px] bg-emerald-100 text-emerald-800">→ {STATUS_LABELS[c.status_changed_to] || c.status_changed_to}</Badge>
                        )}
                      </div>
                      <div className="text-[11px] text-slate-500">{c.agent_name || 'KR'} · {new Date(c.created_at).toLocaleString()}</div>
                      {c.note && <div className="whitespace-pre-wrap mt-1">{c.note}</div>}
                      {c.callback_date && <div className="text-[11px] text-emerald-700 mt-0.5">Call-back scheduled {c.callback_date}</div>}
                    </li>
                  ))}
                </ul>
              </CardContent></Card>
            )}

            {p.notes?.length > 0 && (
              <Card><CardContent className="p-5">
                <SectionTitle>Internal notes</SectionTitle>
                <ul className="space-y-3 mt-2">
                  {p.notes.map((n, i) => (
                    <li key={i} className="text-sm text-slate-700 border-l-2 border-slate-200 pl-3">
                      <div className="text-[11px] text-slate-500">{n.author_name || 'KR'} · {new Date(n.created_at).toLocaleString()}</div>
                      <div className="whitespace-pre-wrap mt-1">{n.body}</div>
                    </li>
                  ))}
                </ul>
              </CardContent></Card>
            )}

            <Card><CardContent className="p-5">
              <SectionTitle>Status history</SectionTitle>
              <ul className="space-y-2 mt-2 text-xs">
                {(p.status_history || []).map((h, i) => (
                  <li key={i} className="text-slate-600">
                    <span className="font-medium">{STATUS_LABELS[h.from_status] || h.from_status || '—'} → {STATUS_LABELS[h.to_status] || h.to_status}</span>
                    <span className="text-slate-400"> · {h.changed_by_name || 'system'} · {new Date(h.changed_at).toLocaleString()}</span>
                  </li>
                ))}
              </ul>
            </CardContent></Card>
          </TabsContent>
        </Tabs>
      </div>

      <ConvertProspectDialog
        open={showConvert}
        onOpenChange={setShowConvert}
        prospect={p}
        prospectId={prospectId}
        onConverted={refresh}
      />

      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent data-testid="prospect-delete-confirm">
          <DialogHeader>
            <DialogTitle>Delete this prospect?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            You are about to permanently remove{' '}
            <strong>{p.first_name} {p.last_name}</strong> ({p.email}). This
            cannot be undone. The audit log will keep a record of who deleted it.
          </p>
          <DialogFooter className="mt-4">
            <Button
              variant="ghost"
              onClick={() => setConfirmDelete(false)}
              disabled={deleting}
              data-testid="prospect-delete-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={doDelete}
              disabled={deleting}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="prospect-delete-confirm-btn"
            >
              {deleting ? 'Deleting…' : 'Yes, delete prospect'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const SectionTitle = ({ children }) => (
  <h3 className="text-xs font-semibold uppercase tracking-wider text-[#b8862d]">{children}</h3>
);

const INVITE_STATUS = {
  pending: { label: 'Awaiting agent', tone: 'bg-amber-100 text-amber-800', icon: Clock },
  completed: { label: 'Completed', tone: 'bg-emerald-100 text-emerald-800', icon: CheckCircle2 },
  cancelled: { label: 'Cancelled', tone: 'bg-slate-200 text-slate-600', icon: XCircle },
  expired: { label: 'Expired', tone: 'bg-rose-100 text-rose-700', icon: AlertTriangle },
};

function ProspectOnboardingCard({ prospectId, prospectStatus }) {
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [freshLink, setFreshLink] = useState('');
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await prospectAPI.onboardingInvite(prospectId);
      setInvite(data.invite || null);
    } catch (_) {
      setInvite(null);
    } finally {
      setLoading(false);
    }
  }, [prospectId]);

  useEffect(() => { load(); }, [load, prospectStatus]);

  const resend = async () => {
    if (!invite?.link_id) return;
    setBusy(true);
    try {
      const { data } = await agentOnboardingAPI.resendLink(invite.link_id);
      setFreshLink(data.onboard_link);
      toast.success(data.email_sent ? `Invite re-emailed to ${data.email}` : 'Fresh link ready — copy and share it');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not resend the invite');
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    if (!freshLink) return;
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(freshLink);
      setCopied(true);
      toast.success('Link copied');
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error('Could not copy — select the link manually'); }
  };

  if (loading) return null;
  if (!invite) return null;
  const meta = INVITE_STATUS[invite.status] || INVITE_STATUS.pending;
  const StatusIcon = meta.icon;
  return (
    <Card data-testid="prospect-onboarding-invite"><CardContent className="p-5">
      <div className="flex items-center justify-between">
        <SectionTitle>Onboarding invite</SectionTitle>
        <Badge className={`text-[10px] ${meta.tone}`} data-testid="prospect-invite-status">
          <StatusIcon size={11} className="mr-1" /> {meta.label}
        </Badge>
      </div>
      <div className="mt-2 text-sm text-slate-700 flex items-start gap-2">
        <Link2 size={14} className="mt-0.5 text-slate-400" />
        <div>
          <div>A self-service onboarding link was sent to <strong>{invite.email}</strong>.</div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            {invite.agency_name} · NPN {invite.npn} · issued {invite.created_at ? new Date(invite.created_at).toLocaleString() : '—'}
            {invite.resent_at ? ` · resent ${new Date(invite.resent_at).toLocaleDateString()}` : ''}
          </div>
          {invite.status === 'completed' && (
            <div className="text-[11px] text-emerald-700 mt-0.5">
              Agent completed onboarding{invite.created_agent_npn ? ` (NPN ${invite.created_agent_npn})` : ''}{invite.completed_at ? ` · ${new Date(invite.completed_at).toLocaleString()}` : ''}
            </div>
          )}
          {invite.status === 'pending' && invite.expires_at && (
            <div className="text-[11px] text-slate-400 mt-0.5">Link expires {new Date(invite.expires_at).toLocaleDateString()}</div>
          )}
        </div>
      </div>
      {invite.status !== 'completed' && (
        <div className="mt-3">
          <Button size="sm" variant="outline" onClick={resend} disabled={busy} data-testid="prospect-invite-resend">
            {busy ? <Loader2 size={13} className="animate-spin mr-1" /> : <Send size={13} className="mr-1" />}
            Resend invite
          </Button>
          {freshLink && (
            <div className="flex gap-2 mt-2">
              <Input readOnly value={freshLink} className="text-xs h-8" data-testid="prospect-invite-fresh-link" />
              <Button type="button" variant="outline" size="icon" className="h-8 w-8 shrink-0" onClick={copyLink} data-testid="prospect-invite-copy">
                {copied ? <CheckCircle2 size={14} /> : <Copy size={14} />}
              </Button>
            </div>
          )}
        </div>
      )}
    </CardContent></Card>
  );
}

const InfoRow = ({ icon, label, value }) => (
  <div className="flex items-start gap-2 text-sm">
    <div className="mt-0.5 text-slate-400">{icon}</div>
    <div><span className="text-slate-500">{label}:</span> <span className="text-slate-800">{value}</span></div>
  </div>
);
